import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "vibe-platform.favorites.v1";

/**
 * Persist a per-user list of favorited app names in localStorage.
 * Storing only the name (not full app object) keeps it stable across
 * environments and prevents stale shape issues.
 */
export function useFavorites() {
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(favorites)));
    } catch {
      // quota exceeded — silently ignore
    }
  }, [favorites]);

  const toggle = useCallback((name: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const isFavorite = useCallback((name: string) => favorites.has(name), [favorites]);

  return { favorites, toggle, isFavorite };
}
